import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, FileText, ExternalLink, Copy, Loader2, Clock, CheckCircle, XCircle, FlaskConical, AlertTriangle, Search, ChevronLeft, ChevronRight, Trash2, RefreshCw, ArrowRight } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const MerchantInvoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ search: '', from_date: '', till_date: '' });
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [conversion, setConversion] = useState(null); // { crypto_amount, rate, source }
  const [converting, setConverting] = useState(false);
  const conversionTimer = useRef(null);
  const [form, setForm] = useState({
    fiatAmount: '', network_id: '', currency: '', wallet_id: '', description: '', expiry_minutes: 30
  });

  const isTestMode = user?.test_mode !== false;
  const LIMIT = 100;

  const fetchInvoices = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filters.search) params.append('search', filters.search);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.till_date) params.append('till_date', filters.till_date);
      const res = await axios.get(`${API}/invoices?${params}`);
      setInvoices(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch { toast.error('Failed to fetch invoices'); }
  }, [filters]);

  const fetchMeta = async () => {
    try {
      const [walletsRes, tokensRes, networksRes, billingRes, currencyRes] = await Promise.all([
        axios.get(`${API}/wallets`),
        axios.get(`${API}/networks/all-tokens`),
        axios.get(`${API}/networks`),
        axios.get(`${API}/billing/status`).catch(() => ({ data: null })),
        axios.get(`${API}/settings/currency`).catch(() => ({ data: { preferred_currency: 'USD' } })),
      ]);
      setWallets(walletsRes.data);
      setTokens(tokensRes.data.filter(t => t.is_active !== false));
      setNetworks(networksRes.data.filter(n => n.is_active !== false));
      if (billingRes.data) setBillingStatus(billingRes.data);
      setPreferredCurrency(currencyRes.data?.preferred_currency || 'USD');
    } catch { /* silent */ }
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { setLoading(true); fetchInvoices(1).finally(() => setLoading(false)); }, [fetchInvoices]);

  // Real-time polling every 5 seconds
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => fetchInvoices(pagination.page), 5000);
    return () => clearInterval(pollingRef.current);
  }, [fetchInvoices, pagination.page]);

  // --- Create Invoice Logic ---
  const activeWallets = wallets.filter(w => w.is_active !== false && w.network_active !== false);
  const networksWithWallets = networks.filter(n => activeWallets.some(w => w.network_id === n.id));
  const getAvailableCurrencies = () => {
    if (!form.network_id) return [];
    const nw = activeWallets.filter(w => w.network_id === form.network_id);
    return tokens.filter(t => t.network_id === form.network_id && nw.some(w => !w.enabled_tokens || w.enabled_tokens.includes(t.id)));
  };
  const getAvailableWallets = () => form.network_id ? activeWallets.filter(w => w.network_id === form.network_id) : [];
  const handleNetworkChange = (v) => { setForm(p => ({ ...p, network_id: v, currency: '', wallet_id: '' })); setConversion(null); };
  const handleCurrencyChange = (currency) => {
    const nw = activeWallets.filter(w => w.network_id === form.network_id);
    const tk = tokens.find(t => t.network_id === form.network_id && t.symbol === currency);
    const cw = nw.filter(w => !w.enabled_tokens || (tk && w.enabled_tokens.includes(tk.id)));
    setForm(p => ({ ...p, currency, wallet_id: cw.length === 1 ? cw[0].id : '' }));
    setConversion(null);
  };

  // Live fiat → crypto conversion (debounced 600ms)
  useEffect(() => {
    if (!form.fiatAmount || !form.currency || !preferredCurrency) { setConversion(null); return; }
    const amt = parseFloat(form.fiatAmount);
    if (!amt || amt <= 0) { setConversion(null); return; }
    clearTimeout(conversionTimer.current);
    conversionTimer.current = setTimeout(async () => {
      setConverting(true);
      try {
        const { data } = await axios.get(`${API}/convert`, {
          params: { fiat_amount: amt, fiat_currency: preferredCurrency, crypto_symbol: form.currency }
        });
        setConversion(data);
      } catch { setConversion({ error: 'Conversion unavailable' }); }
      finally { setConverting(false); }
    }, 600);
    return () => clearTimeout(conversionTimer.current);
  }, [form.fiatAmount, form.currency, preferredCurrency]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.wallet_id) { toast.error('Please select a wallet'); return; }
    if (!form.currency) { toast.error('Please select a currency'); return; }
    if (!form.fiatAmount || parseFloat(form.fiatAmount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!conversion || conversion.error) { toast.error('Wait for currency conversion to complete'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/invoices`, {
        amount: conversion.crypto_amount,
        currency: form.currency,
        wallet_id: form.wallet_id,
        description: form.description,
        expiry_minutes: parseInt(form.expiry_minutes),
        fiat_amount: parseFloat(form.fiatAmount),
        fiat_currency: preferredCurrency,
      });
      toast.success('Invoice created!');
      setDialogOpen(false);
      setForm({ fiatAmount: '', network_id: '', currency: '', wallet_id: '', description: '', expiry_minutes: 30 });
      setConversion(null);
      fetchInvoices(1);
      window.open(`/checkout/${res.data.id}`, '_blank');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create invoice'); }
    finally { setCreating(false); }
  };

  // --- Helpers ---
  const copyToClipboard = (text) => { if (text && text !== '—') { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); } };
  const copyLink = (id) => { navigator.clipboard.writeText(`${window.location.origin}/checkout/${id}`); toast.success('Link copied!'); };
  const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const handleSearch = (e) => { e.preventDefault(); setSelected([]); fetchInvoices(1); };

  // --- Selection for deletable invoices (expired + test paid) ---
  const isDeletable = (i) => i.status === 'expired' || (i.status === 'paid' && i.is_test);
  const deletableInvoices = invoices.filter(isDeletable);
  const allDeletableSelected = deletableInvoices.length > 0 && deletableInvoices.every(i => selected.includes(i.id));
  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (allDeletableSelected) setSelected([]);
    else setSelected(deletableInvoices.map(i => i.id));
  };
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!confirm(`Delete ${selected.length} invoice(s)?`)) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/invoices/bulk`, { data: { invoice_ids: selected } });
      toast.success(`Deleted ${res.data.deleted} invoice(s)`);
      setSelected([]);
      fetchInvoices(pagination.page);
    } catch { toast.error('Failed to delete invoices'); }
    finally { setDeleting(false); }
  };

  const getStatusBadge = (status) => {
    const map = {
      paid: ['bg-emerald-500/20 text-emerald-500', <CheckCircle key="i" className="h-3 w-3 mr-1" />, 'Paid'],
      overpaid: ['bg-emerald-500/20 text-emerald-500', <CheckCircle key="i" className="h-3 w-3 mr-1" />, 'Overpaid'],
      partially_paid: ['bg-amber-500/20 text-amber-500', <AlertTriangle key="i" className="h-3 w-3 mr-1" />, 'Partially Paid'],
      underpaid: ['bg-amber-500/20 text-amber-500', <AlertTriangle key="i" className="h-3 w-3 mr-1" />, 'Underpaid'],
      unpaid: ['bg-destructive/20 text-destructive', <XCircle key="i" className="h-3 w-3 mr-1" />, 'Unpaid'],
      expired: ['bg-destructive/20 text-destructive', <XCircle key="i" className="h-3 w-3 mr-1" />, 'Expired'],
      cancelled: ['bg-muted text-muted-foreground', <XCircle key="i" className="h-3 w-3 mr-1" />, 'Cancelled'],
    };
    const [cls, icon, label] = map[status] || ['bg-yellow-500/20 text-yellow-500', <Clock key="i" className="h-3 w-3 mr-1" />, 'Pending'];
    return <Badge className={cls}>{icon}{label}</Badge>;
  };

  return (
    <MerchantLayout title="Invoices">
      <div className="space-y-6" data-testid="invoices-page">
        {/* Billing Suspension Banner */}
        {billingStatus?.is_suspended && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="invoice-suspended-banner">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-400">Invoice Creation Disabled</p>
              <p className="text-xs text-red-400/80 mt-0.5">Your account is suspended due to unpaid billing invoices. Please pay your outstanding invoices to resume creating new invoices.</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pb-2">
          <p className="text-muted-foreground">Create and manage payment invoices</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-white" disabled={activeWallets.length === 0 || billingStatus?.is_suspended} data-testid="create-invoice-dialog-btn">
                <Plus className="h-4 w-4 mr-2" /> Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Invoice</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Amount ({preferredCurrency})</Label>
                  <div className="relative">
                    <Input
                      type="number" step="0.01" min="0.01"
                      placeholder={`100.00 ${preferredCurrency}`}
                      value={form.fiatAmount}
                      onChange={(e) => setForm({ ...form, fiatAmount: e.target.value })}
                      required
                      data-testid="invoice-amount-input"
                    />
                  </div>
                  {/* Live conversion preview */}
                  {form.fiatAmount && form.currency && (
                    <div className={`flex items-center gap-2 p-2.5 rounded-lg text-xs border ${
                      conversion?.error ? 'border-red-500/20 bg-red-500/5 text-red-400' :
                      conversion ? 'border-primary/20 bg-primary/5 text-foreground' :
                      'border-border bg-muted/30 text-muted-foreground'
                    }`} data-testid="conversion-preview">
                      {converting ? (
                        <><RefreshCw className="h-3 w-3 animate-spin shrink-0 text-muted-foreground" /><span>Converting...</span></>
                      ) : conversion?.error ? (
                        <span>{conversion.error}</span>
                      ) : conversion ? (
                        <><span className="text-muted-foreground">{form.fiatAmount} {preferredCurrency}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="font-semibold text-foreground">{conversion.crypto_amount} {form.currency}</span>
                        <span className="text-muted-foreground ml-auto">via {conversion.source}</span></>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="space-y-2"><Label>Network</Label>
                  <Select value={form.network_id} onValueChange={handleNetworkChange}><SelectTrigger data-testid="invoice-network-select"><SelectValue placeholder="Select network" /></SelectTrigger>
                    <SelectContent>{networksWithWallets.map(n => <SelectItem key={n.id} value={n.id}>{n.name} ({n.symbol})</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Currency</Label>
                  <Select value={form.currency} onValueChange={handleCurrencyChange} disabled={!form.network_id}><SelectTrigger data-testid="invoice-currency-select"><SelectValue placeholder={form.network_id ? "Select currency" : "Select network first"} /></SelectTrigger>
                    <SelectContent>{getAvailableCurrencies().map(t => <SelectItem key={t.id} value={t.symbol}>{t.symbol} — {t.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Receiving Wallet</Label>
                  <Select value={form.wallet_id} onValueChange={(v) => setForm({ ...form, wallet_id: v })} disabled={!form.network_id}><SelectTrigger data-testid="invoice-wallet-select"><SelectValue placeholder={form.network_id ? "Select wallet" : "Select network first"} /></SelectTrigger>
                    <SelectContent>{getAvailableWallets().map(w => <SelectItem key={w.id} value={w.id}>{w.label} ({w.address.slice(0, 6)}...{w.address.slice(-4)})</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-2"><Label>Description (Optional)</Label>
                  <Input placeholder="Order #12345" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-description-input" />
                </div>
                <div className="space-y-2"><Label>Expiry (minutes)</Label>
                  <Select value={form.expiry_minutes.toString()} onValueChange={(v) => setForm({ ...form, expiry_minutes: v })}><SelectTrigger data-testid="invoice-expiry-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="15">15 minutes</SelectItem><SelectItem value="30">30 minutes</SelectItem><SelectItem value="60">1 hour</SelectItem><SelectItem value="1440">24 hours</SelectItem></SelectContent></Select>
                </div>
                <Button type="submit" className="w-full bg-primary text-white" disabled={creating} data-testid="invoice-submit-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : isTestMode ? 'Create Test Invoice' : 'Create Invoice'}
                </Button>
                {isTestMode && <p className="text-xs text-amber-500 text-center flex items-center justify-center gap-1"><FlaskConical className="h-3 w-3" /> Test mode</p>}
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search & Filters */}
        <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3" data-testid="invoice-filters">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Amount, Wallet, TXN ID, Invoice ID..." className="pl-9" value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} data-testid="invoice-search-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">From Date</Label>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters(f => ({ ...f, from_date: e.target.value }))} className="w-[160px]" data-testid="invoice-from-date" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Till Date</Label>
            <Input type="date" value={filters.till_date} onChange={(e) => setFilters(f => ({ ...f, till_date: e.target.value }))} className="w-[160px]" data-testid="invoice-till-date" />
          </div>
          <Button type="submit" size="sm" className="bg-primary text-white" data-testid="invoice-search-btn">
            <Search className="h-4 w-4 mr-1" /> Search
          </Button>
          {(filters.search || filters.from_date || filters.till_date) && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setFilters({ search: '', from_date: '', till_date: '' }); }} data-testid="invoice-clear-filters">Clear</Button>
          )}
        </form>

        {/* Data Table */}
        <Card className="border-border bg-card">
          {selected.length > 0 && (
            <div className="flex items-center justify-between px-4 py-2 bg-destructive/10 border-b border-border">
              <span className="text-sm text-destructive font-medium">{selected.length} invoice(s) selected</span>
              <Button size="sm" variant="destructive" disabled={deleting} onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />} Delete Selected
              </Button>
            </div>
          )}
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : invoices.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-base font-semibold mb-2">No Invoices Found</h3>
                <p className="text-muted-foreground text-sm">Create your first invoice or adjust your filters</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]">
                          <Checkbox checked={allDeletableSelected} onCheckedChange={toggleSelectAll} title="Select all deletable" data-testid="select-all-expired" />
                        </TableHead>
                        <TableHead className="w-[160px]">Date & Time</TableHead>
                        <TableHead>Invoice ID</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Payable</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">TX Fee</TableHead>
                        {invoices.some(inv => (inv.tx_cost || 0) > 0) && (
                          <TableHead className="text-right">TX Cost</TableHead>
                        )}
                        <TableHead>Sender Wallet</TableHead>
                        <TableHead>TXN ID</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.id} className={selected.includes(inv.id) ? 'bg-destructive/5' : ''} data-testid={`invoice-row-${inv.id}`}>
                          <TableCell>
                            {isDeletable(inv) ? (
                              <Checkbox checked={selected.includes(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} data-testid={`select-invoice-${inv.id}`} />
                            ) : <span className="w-4" />}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(inv.created_at)}</TableCell>
                          <TableCell>
                            <span className="font-mono text-xs cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(inv.id)} title="Click to copy" data-testid={`copy-id-${inv.id}`}>
                              {inv.id.slice(0, 12)}... <Copy className="h-3 w-3 inline opacity-40" />
                            </span>
                            {inv.is_test && <Badge variant="outline" className="ml-1 text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20 px-1 py-0">TEST</Badge>}
                            {(inv.source === 'api' || inv.source === 'payment_link') && <Badge variant="outline" className="ml-1 text-[10px] bg-blue-500/10 text-blue-500 border-blue-500/20 px-1 py-0">API</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">
                            {inv.amount} <span className="text-muted-foreground text-xs">{inv.currency}</span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`payable-${inv.id}`}>
                            {inv.payable_amount != null && inv.payable_amount !== inv.amount
                              ? <span className={inv.payable_amount > inv.amount ? 'text-emerald-400' : 'text-amber-400'}>{Number(inv.payable_amount).toFixed(3)}</span>
                              : <span className="text-muted-foreground">{Number(inv.amount).toFixed(3)}</span>}
                          </TableCell>
                          <TableCell>{getStatusBadge(inv.status)}</TableCell>
                          <TableCell className="text-right font-mono text-xs text-muted-foreground" data-testid={`tx-fee-${inv.id}`}>
                            {inv.tx_fee != null && inv.tx_fee > 0 ? inv.tx_fee.toFixed(4) : ''}
                          </TableCell>
                          {invoices.some(inv => (inv.tx_cost || 0) > 0) && (
                            <TableCell className="text-right font-mono text-xs text-muted-foreground" data-testid={`tx-cost-${inv.id}`}>
                              {inv.tx_cost != null && inv.tx_cost > 0 ? inv.tx_cost.toFixed(4) : ''}
                            </TableCell>
                          )}
                          <TableCell>
                            {inv.sender_wallet ? (
                              <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(inv.sender_wallet)} title="Click to copy">
                                {inv.sender_wallet.slice(0, 6)}...{inv.sender_wallet.slice(-4)} <Copy className="h-3 w-3 inline opacity-40" />
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            {inv.tx_hash ? (
                              <span className="font-mono text-xs text-muted-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => copyToClipboard(inv.tx_hash)} title="Click to copy">
                                {inv.tx_hash.slice(0, 10)}... <Copy className="h-3 w-3 inline opacity-40" />
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyLink(inv.id)} title="Copy checkout link" data-testid={`copy-link-${inv.id}`}>
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <a href={`/checkout/${inv.id}`} target="_blank" rel="noopener noreferrer">
                                <Button size="icon" variant="ghost" className="h-7 w-7" title="Open checkout" data-testid={`view-invoice-${inv.id}`}>
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Button>
                              </a>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Showing {(pagination.page - 1) * LIMIT + 1}–{Math.min(pagination.page * LIMIT, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={pagination.page <= 1} onClick={() => fetchInvoices(pagination.page - 1)} data-testid="invoice-prev-page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">Page {pagination.page} of {pagination.pages}</span>
                    <Button size="sm" variant="outline" disabled={pagination.page >= pagination.pages} onClick={() => fetchInvoices(pagination.page + 1)} data-testid="invoice-next-page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </MerchantLayout>
  );
};

export default MerchantInvoices;
